import AdminRequestMobileCard from "../components/AdminRequestMobileCard";
import "./AdminMobileResponsive.css";
import { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import RequestRow, { AttentionDot } from "../components/RequestRow";
import useLiveRequests from "../components/useLiveRequests";
import { Home, ChevronRight, Search, ArrowUpDown, ChevronLeft, Trash2, RotateCcw } from "lucide-react";
import api from "../api";
import { pageWindow, byUrgency, isOverdue } from "../quoteUtils";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "attention", label: "Needs attention" },
  { key: "awaiting_reply", label: "Reply needed" },
  { key: "quote", label: "Quote needed" },
  { key: "revision_requested", label: "Revision requested" },
  { key: "Pending", label: "Pending" },
  { key: "Quoted", label: "Quoted" },
  { key: "Approved", label: "Approved" },
  { key: "Rejected", label: "Declined" },
];

const matches = (r, f) => {
  switch (f) {
    case "all": return true;
    case "attention": return !!r.attention;
    case "awaiting_reply": return r.attention?.reason === "awaiting_reply";
    case "quote": return ["needs_quote", "draft_not_sent"].includes(r.attention?.reason);
    case "revision_requested": return r.attention?.reason === "revision_requested";
    default: return r.status === f;
  }
};

export default function AdminRequests() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { requests, loading, error, reload } = useLiveRequests();
  const [trashView, setTrashView] = useState(false);
  const [trashItems, setTrashItems] = useState([]);
  const [actionError, setActionError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const admin = (() => { try { return JSON.parse(localStorage.getItem("user") || "null")?.role === "admin"; } catch { return false; } })();
  const refreshTrash = async () => { try { const { data } = await api.get("/admin/requests/trash"); setTrashItems(Array.isArray(data) ? data : []); setActionError(""); } catch (e) { setActionError(e.response?.data?.message || "Could not load trash."); } };
  useEffect(() => { if (admin && trashView) refreshTrash(); }, [trashView, admin]);
  const updateTrash = async (r, restore = false) => {
    if (!restore && !window.confirm(`Move request ${r.requestId || r._id} to Trash? You can restore it later.`)) return;
    setBusyId(r._id); setActionError("");
    try { await api.patch(`/admin/requests/${r._id}/${restore ? "restore" : "trash"}`); await Promise.all([reload(), refreshTrash()]); }
    catch (e) { setActionError(e.response?.data?.message || "Request update failed."); }
    finally { setBusyId(null); }
  };
  const [search, setSearch] = useState(() => params.get("q") || "");
  useEffect(() => { setSearch(params.get("q") || ""); }, [params]);
  const [sort, setSort] = useState("urgent"); // urgent | newest | oldest
  const [page, setPage] = useState(1);
  const filter = params.get("attention") ? "attention" : params.get("filter") || "all";
  const setFilter = (f) => setParams(f === "all" ? {} : f === "attention" ? { attention: "1" } : { filter: f });
  const limit = 10;

  const counts = useMemo(() => Object.fromEntries(FILTERS.map((f) => [f.key, requests.filter((r) => matches(r, f.key)).length])), [requests]);
  const overdue = useMemo(() => requests.filter((r) => isOverdue(r.attention)).length, [requests]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = (trashView ? trashItems : requests).filter((r) => trashView || matches(r, filter));
    if (q) {
      list = list.filter((r) =>
        [r._id, r.requestId, r.customerName, r.customerContact, r.rego, r.vin, r.partRequired, r.partNumber, r.notes, r.quotedByName]
          .filter(Boolean).some((f) => String(f).toLowerCase().includes(q))
      );
    }
    const newest = (a, b) => new Date(b.createdAt) - new Date(a.createdAt);
    if (sort === "urgent") {
      // needs-attention first (longest waiting at the top), then newest
      return [...list].sort((a, b) => (!!b.attention - !!a.attention) || (a.attention && b.attention ? byUrgency(a, b) : newest(a, b)));
    }
    return [...list].sort(sort === "newest" ? newest : (a, b) => -newest(a, b));
  }, [requests, trashItems, trashView, search, sort, filter]);

  useEffect(() => { setPage(1); }, [search, sort, filter, trashView]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const pageItems = filtered.slice((page - 1) * limit, page * limit);
  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const nextSort = { urgent: "newest", newest: "oldest", oldest: "urgent" };
  const sortLabel = { urgent: "Needs attention first", newest: "Newest", oldest: "Oldest" };

  return (
    <AdminLayout>
      <main className="p-6 space-y-6 agp-admin-responsive agp-admin-requests">
        <div>
          <div className="flex justify-between items-center gap-3"><h1 className="text-3xl font-extrabold text-slate-800">{trashView ? "Trashed Requests" : "Requests"}</h1>{admin && <button onClick={() => setTrashView(!trashView)} className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold"><Trash2 size={15}/>{trashView ? "Back to Requests" : "View Trash"}</button>}</div>
          {actionError && <p role="alert" className="mt-2 text-red-600 text-sm">{actionError}</p>}
          <div className="flex items-center gap-2 text-sm text-slate-500 mt-2">
            <Home size={14} />
            <button onClick={() => navigate("/admin/dashboard")} className="hover:text-slate-700">Dashboard</button>
            <ChevronRight size={14} />
            <span className="font-semibold text-slate-700">Requests</span>
          </div>
        </div>

        {!trashView && counts.attention > 0 && filter !== "attention" && (
          <button onClick={() => setFilter("attention")}
            className={`w-full flex items-center gap-3 text-left rounded-xl px-4 py-3 text-sm border ${overdue ? "bg-red-50 border-red-200 text-red-800" : "bg-amber-50 border-amber-200 text-amber-900"}`}>
            <AttentionDot overdue={overdue > 0} />
            <span className="flex-1">
              <b>{counts.attention}</b> request{counts.attention === 1 ? "" : "s"} need attention
              {overdue > 0 && <> · <b>{overdue}</b> overdue</>}
            </span>
            <span className="font-semibold underline">Show them</span>
          </button>
        )}

        <div className="bg-white rounded-xl border border-gray-100 p-6">
          {!trashView && <div className="flex gap-2 flex-wrap mb-4">
            {FILTERS.map((f) => (
              <button key={f.key} onClick={() => setFilter(f.key)} aria-pressed={filter === f.key}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold ${filter === f.key ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                {f.label} <span className="opacity-60">{counts[f.key]}</span>
              </button>
            ))}
          </div>}

          <div className="flex flex-col md:flex-row gap-3 mb-5">
            <div className="flex-1 relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by request ID, customer, vehicle, part, staff..."
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button onClick={() => setSort((s) => nextSort[s])}
              className="flex items-center gap-2 px-4 py-3 rounded-lg border border-gray-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">
              <ArrowUpDown size={16} /> Sort: {sortLabel[sort]}
            </button>
          </div>

          <div className="agp-admin-mobile-cards">
            {loading && <p className="text-sm text-slate-500">Loading requests…</p>}
            {!loading && error && <p role="alert" className="text-red-500">{error}</p>}
            {!loading && !error && pageItems.map(r => <AdminRequestMobileCard key={r._id} request={r} onOpen={trashView ? null : () => navigate(`/admin/requests/${r._id}`)} actions={admin ? <button disabled={busyId === r._id} onClick={() => updateTrash(r, trashView)} className="text-sm inline-flex items-center gap-2 text-slate-700">{trashView ? <RotateCcw size={15} /> : <Trash2 size={15} />}{trashView ? "Restore request" : "Move to Trash"}</button> : null} />)}
            {!loading && !error && !pageItems.length && <p className="py-5 text-center text-sm text-slate-500">No requests found.</p>}
          </div>
          <div className="overflow-x-auto agp-admin-desktop-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-white text-xs uppercase bg-slate-900 rounded">
                  <th className="py-4 px-4 font-medium rounded-l-lg">Request ID</th>
                  <th className="py-4 px-4 font-medium">Customer</th>
                  <th className="py-4 px-4 font-medium">Rego/VIN</th>
                  <th className="py-4 px-4 font-medium">Part Required</th>
                  <th className="py-4 px-4 font-medium">Voice/Photo Notes</th>
                  <th className="py-4 px-4 font-medium">Commenting</th>
                  <th className="py-4 px-4 font-medium">Notes</th>
                  <th className="py-4 px-4 rounded-r-lg">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={8} className="py-10 text-center text-slate-400">Loading…</td></tr>}
                {!loading && error && <tr><td colSpan={8} className="py-10 text-center text-red-500">{error}</td></tr>}
                {!loading && !error && pageItems.map((r) => (
                  <RequestRow key={r._id} r={r} onClick={() => !trashView && navigate(`/admin/requests/${r._id}`)} actions={admin ? <button disabled={busyId === r._id} onClick={(e) => { e.stopPropagation(); updateTrash(r, trashView); }} title={trashView ? "Restore request" : "Move to Trash"} className="inline-flex gap-1 items-center rounded-md border px-2 py-1 text-xs text-slate-700 hover:bg-slate-100 disabled:opacity-40">{trashView ? <RotateCcw size={14}/> : <Trash2 size={14}/>} {trashView ? "Restore" : "Trash"}</button> : null} />
                ))}
                {!loading && !error && pageItems.length === 0 && (
                  <tr><td colSpan={8} className="py-10 text-center text-slate-400">
                    {filter === "attention" ? "Nothing needs attention. All caught up." : "No requests found."}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-5">
            <div className="text-sm text-slate-500">Showing <b>{start}</b> to <b>{end}</b> of <b>{total}</b> requests</div>
            <div className="flex items-center gap-1">
              <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} aria-label="Previous page"
                className="p-2 rounded-lg border border-gray-200 disabled:opacity-40"><ChevronLeft size={16} /></button>
              {pageWindow(page, totalPages).map((p) => (
                <button key={p} onClick={() => setPage(p)}
                  className={`w-9 h-9 rounded-lg text-sm font-semibold ${p === page ? "bg-blue-100 text-blue-700" : "border border-gray-200 text-slate-600 hover:bg-slate-50"}`}>{p}</button>
              ))}
              <button disabled={page === totalPages} onClick={() => setPage((p) => p + 1)} aria-label="Next page"
                className="p-2 rounded-lg border border-gray-200 disabled:opacity-40 rotate-180"><ChevronLeft size={16} /></button>
            </div>
          </div>
        </div>
      </main>
    </AdminLayout>
  );
}
